import React, { useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";

import { useMutation, useQuery } from "@apollo/client";
import { useRouteMatch, Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { toast } from "react-toastify";
import {
  QUERY_CLIENT_SENDGRID_FOR_NAVBAR,
  QUERY_CLIENT_SENDGRID_TEMPLATES,
  QUERY_EMAIL_TEMPLATE_OPTIONS,
} from "../../../../config/graphql/query";
import { DELETE_CLIENT_SENDGRID_TEMPLATE } from "../../../../config/graphql/mutation";

import Table from "../../../../components/Table";

import { useClientId } from "../hooks/useClient";
import useHasRole from "../../../../lib/hooks/useHasRole";

const TableRow = ({
  item,
  index,
}: {
  item: IClientSendGridTemplate;
  index: number;
}) => {
  const { url } = useRouteMatch();

  const clientId = useClientId();

  const { sendgridId } = useParams<{ sendgridId: string }>();

  const { t } = useTranslation(["client", "common"]);

  const { id, title, template, templateId } = item;

  const [visible, setVisible] = useToggle(false);

  const [onDelete] = useMutation(DELETE_CLIENT_SENDGRID_TEMPLATE, {
    refetchQueries: [
      {
        query: QUERY_CLIENT_SENDGRID_TEMPLATES,
        variables: {
          filter: { sendgridClient: sendgridId },
        },
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
      {
        query: QUERY_EMAIL_TEMPLATE_OPTIONS,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    variables: { id },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onBeforeDelete = () => {
    onDelete()
      .then(() => {
        toast.success<string>(
          t("client:client.sendgrid.template.toast.deleted"),
        );
      })
      .catch((error) => {
        toast.error<string>(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
        );
      });

    setVisible(false);
  };

  return (
    <>
      <tr>
        <th scope="row">{index + 1}</th>
        <td>{title}</td>
        <td>{template}</td>
        <td>{templateId}</td>
        <td>
          <div className="d-flex">
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
            <Link to={`${url}/${id}`}>
              <Button size="sm" variant="primary">
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.sendgrid.modal.template.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.sendgrid.modal.template.body", { id })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const EmailTemplates = React.memo(() => {
  const { url } = useRouteMatch();

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);
  const clientBasePath = isAdmin ? `/clients/${clientId}` : `/client`;

  const { sendgridId } = useParams<{ sendgridId: string }>();

  const { t } = useTranslation(["client", "common"]);

  const { data: sendGridClientData } = useQuery<{
    sendGridClient: IClientSendGrid;
  }>(QUERY_CLIENT_SENDGRID_FOR_NAVBAR, {
    skip: !sendgridId,
    variables: { id: sendgridId },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const { data } = useQuery(QUERY_CLIENT_SENDGRID_TEMPLATES, {
    fetchPolicy: "network-only",
    skip: !sendgridId,
    variables: {
      filter: {
        sendgridClient: sendgridId,
      },
    },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const sendGridTemplateArr = data?.emailTemplates ?? [];

  const renderClientToken = useCallback(
    (item: IClientSendGridTemplate, index: number) => (
      <TableRow key={item.id} item={item} index={index} />
    ),
    [],
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          {isAdmin && (
            <li className="breadcrumb-item active" aria-current="page">
              <Link to="/clients">{t("client:client.token.nav.clients")}</Link>
            </li>
          )}
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={clientBasePath}>
              {sendGridClientData?.sendGridClient?.client?.title ?? "Client"}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={`${clientBasePath}/sendgrid`}>
              {t("client:client.sendgrid.nav.sendgrid")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {isAdmin ? (
              <Link to={`/clients/${clientId}/sendgrid/${sendgridId}`}>
                {sendGridClientData?.sendGridClient.title ?? "Title"}
              </Link>
            ) : (
              sendGridClientData?.sendGridClient.title ?? "Title"
            )}
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {t("client:client.sendgrid.nav.templates")}
          </li>
        </ol>
      </nav>
      <div className="d-flex justify-content-end align-items-center my-4">
        <div>
          <Link to={`${url}/new`} type="button" className="btn btn-primary">
            {t("client:client.sendgrid.template.button.add")}
          </Link>
        </div>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">{t("client:client.sendgrid.template.th.title")}</th>
            <th scope="col">
              {t("client:client.sendgrid.template.th.template")}
            </th>
            <th scope="col">
              {t("client:client.sendgrid.template.th.templateid")}
            </th>
            <th scope="col">{t("client:client.tokens.th.actions")}</th>
          </tr>
        </thead>
        <tbody>{sendGridTemplateArr.map(renderClientToken)}</tbody>
      </Table>
    </div>
  );
});

export default EmailTemplates;
